import { Component, computed, effect, inject, ChangeDetectionStrategy, untracked } from '@angular/core';
import { AspectCache, InputCache, PrototypeCache, TypeCache } from '@xanadu/cache';
import { FormGroup } from '@angular/forms';
import { PanelComponent, Register } from '@xanadu/core';
import { SafeStyle } from '@angular/platform-browser';
import { TreeSignal } from '@xanadu/signal';
import { Type } from '@xanadu/model';

@Component({
    selector: 'app-type-panel',
    templateUrl: './type-panel.component.html',
    styleUrls: ['./type-panel.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[style]': 'hostStyle'
    }
})
@Register('type-panel')
export class TypePanelComponent extends PanelComponent<Type> {
    private readonly typeCache = inject(TypeCache);
    private readonly inputCache = inject(InputCache);
    private readonly aspectCache = inject(AspectCache);
    private readonly prototypeCache = inject(PrototypeCache);
    private readonly treeSignal = inject(TreeSignal);

    private tabType = { caption: 'Type', active: true, hidden: false };

    hostStyle?: SafeStyle;

    protected override get nodeType(): string | undefined {
        return 'type';
    }

    readonly types = computed(() => {
        const types = this.typeCache.signal();
        return types ? [...types.values()] : undefined;
    });

    readonly inputs = computed(() => {
        const inputs = this.inputCache.signal();
        return inputs ? [...inputs.values()] : undefined;
    });

    meta: any = { id: 'model', name: 'Data Type', display: 'name', mandatory: true, alwaysMatch: true };

    formReady: boolean = false;
    dataGroup?: FormGroup;
    views: { child: any, meta: any }[] = [];

    constructor(
    ) {
        super();

        this.hostStyle = 'height: 100%';

        effect(() => {
            const inputId = this.treeSignal.input();
            const typeModel = untracked(() => this.node());

            if (typeModel?.isNew) {
                const inputs = untracked(() => this.inputs());

                const input = inputs?.find(x => x._id == inputId);
                if (input && typeModel) {
                    typeModel.input = input;
                    typeModel.inputId = inputId;
                    typeModel.graphqlType = input.graphqlType;

                    if (this.views.length > 1) {
                        this.views.splice(1);
                    }

                    this.loadGroups(input);
                }
            }
        });
    }

    protected override setup(type: Type): void {
        super.setup(type);

        this.tabs = [
            this.tabType
        ];
        this.tabSelected = this.tabType;

        this.loadView();
    }

    protected override load(type: Type, form: any): string | undefined {
        super.load(type, form);

        if (form && type) {
            type.data = form.data;
            type.name = form.name;
        }

        return;
    }

    protected override refresh(type: Type): void {
        super.refresh(type);

        this.typeCache.update();

        this.aspectCache.update();
        this.prototypeCache.update();
    }

    private loadView(): void {
        this.formReady = false;

        this.dataGroup = new FormGroup({});
        this.formGroup()?.addControl('data', this.dataGroup);
        this.setViews();

        this.formReady = true;
    }

    private setViews(): void {
        const typeModel = this.node();
        const result: { child: any, meta: any }[] =
            [{ child: { type: typeModel, meta: { inputs: this.inputs(), formGroup: this.formGroup() }, component: 'common-meta' }, meta: { title: 'Common', description: 'Data fields common to all data types' } }];

        this.views = result;
        this.loadGroups();
    }

    private loadGroups(input: any = null) {
        const typeModel = this.node();

        if (!input && typeModel) {
            input = typeModel.input;
        }

        if (typeModel?.input?.group && input?.group) {
            for (const item of typeModel.input.group) {
                const view: any = { child: { type: typeModel, meta: { formGroup: this.dataGroup }, component: item.component }, meta: item.meta };
                this.views.push(view);
            }
        }
    }
}
